import { AxiosHeaders } from 'axios'
import type { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import { useTokenStore } from '@/features/auth/store'
import { parseAccountGate } from '@/features/account/account-gate.data'
import { useAccountGateStore } from '@/features/account/store/useAccountGateStore'
import { isProvisioningUnavailable, provisioningRetryDelayMs } from './provisioningRetry'

interface StoredTokens {
  accessToken?: string
  refreshToken?: string
  expiresIn?: number
}

const REFRESH_ENDPOINT = 'auth/refresh_tokens'

/**
 * Routes whose 401 means "wrong credentials", not "expired token" (audit 2026-10-19 #5): the
 * API checks the password by signing in with it. Refreshing and REPLAYING such a call repeats
 * the failed sign-in — two Cognito failures per wrong password (lockout counter). An expired
 * token is still renewed BEFORE these calls by the request interceptor (local expiry).
 */
const CREDENTIAL_CHECK_ENDPOINTS: readonly string[] = ['auth/login', 'auth/change-password']

/** `url` names a route whose 401 is a credential refusal — never refresh-and-replay it. */
export const isCredentialCheckRequest = (url: string | undefined): boolean => {
  const path = (url ?? '').split(/[?#]/)[0].replace(/^\/+/, '').replace(/\/+$/, '')
  return CREDENTIAL_CHECK_ENDPOINTS.includes(path)
}
const isClient = typeof window !== 'undefined'

const getStoredTokens = (): StoredTokens => {
  if (!isClient) {
    return {}
  }

  try {
    const tokens = window.localStorage.getItem('userTokens')
    return tokens ? JSON.parse(tokens) : {}
  } catch {
    return {}
  }
}

const ensureHeader = (config: InternalAxiosRequestConfig, key: string, value: string) => {
  if (!config.headers) {
    config.headers = new AxiosHeaders()
  }
  config.headers[key] = value
}

export const attachAuthInterceptors = (client: AxiosInstance): void => {
  client.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      const tokenStore = useTokenStore()

      if (isClient) {
        const currency = window.localStorage.getItem('currency') ?? 'USD'
        ensureHeader(config, 'Currency', currency)

        const locale = window.localStorage.getItem('userLocale') ?? 'en'
        ensureHeader(config, 'lang', locale)
        // The backend localises labels by `Accept-Language` only (default `ru`), not by `lang`:
        // without it an English UI in a Russian-locale browser gets Russian server labels.
        ensureHeader(config, 'Accept-Language', locale)
      } else {
        ensureHeader(config, 'Currency', 'USD')
        ensureHeader(config, 'lang', 'en')
        ensureHeader(config, 'Accept-Language', 'en')
      }

      if (tokenStore.accessToken && config.url !== REFRESH_ENDPOINT) {
        const storedTokens = getStoredTokens()
        if (Date.now() > (storedTokens.expiresIn ?? 0)) {
          // Single-flight inside the store: concurrent expired requests all
          // await one refresh instead of racing or skipping it.
          await tokenStore.updateTokens()
        }
      }

      // Attached after the refresh so a renewed token is the one sent
      if (tokenStore.accessToken) {
        ensureHeader(config, 'Authorization', `Bearer ${tokenStore.accessToken}`)
      }

      return config
    },
    (error: AxiosError) => Promise.reject(error)
  )

  client.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (
      error: AxiosError & {
        config: InternalAxiosRequestConfig & { _retry?: boolean; _provisioningAttempts?: number }
      },
    ) => {
      const tokenStore = useTokenStore()
      const originalRequest = error.config

      const responseStatus = error.response?.status
      const responseData = (error.response?.data ?? {}) as unknown

      // 503 provisioning_unavailable: our side failed, the token is fine — wait, repeat, never sign out
      const attempts = originalRequest?._provisioningAttempts ?? 0
      const wait = originalRequest
        ? provisioningRetryDelayMs(responseStatus, responseData, error.response?.headers?.['retry-after'], attempts)
        : null
      if (wait !== null) {
        originalRequest._provisioningAttempts = attempts + 1
        await new Promise((resolve) => setTimeout(resolve, wait))
        return client(originalRequest)
      }
      // Decision 8.3: the repeats are spent (or the window is too long to wait inside a request) —
      // the session host says "Try again". Still never a sign-out, never a token refresh.
      if (isProvisioningUnavailable(responseStatus, responseData)) {
        useAccountGateStore().reportProvisioningUnavailable()
        return Promise.reject(error)
      }

      const isAccountMerge =
        responseStatus === 401 &&
        typeof responseData === 'object' &&
        responseData !== null &&
        'includes' in responseData &&
        typeof (responseData as { includes: (value: string) => boolean }).includes === 'function' &&
        (responseData as { includes: (value: string) => boolean }).includes('accounts have been successfully merged')

      if (isAccountMerge) {
        return Promise.reject(error)
      }

      // Decisions 15 / 16: a revoked token (password changed elsewhere — the refresh token died
      // with it) and a scheduled-for-deletion account end the session here. Never refreshed or
      // replayed: the session host signs out locally and shows the message / restore screen.
      const gate = parseAccountGate(responseStatus, responseData)
      if (gate) {
        useAccountGateStore().report(gate)
        return Promise.reject(error)
      }

      if (
        responseStatus === 401 &&
        !originalRequest._retry &&
        originalRequest.url !== REFRESH_ENDPOINT &&
        !isCredentialCheckRequest(originalRequest.url)
      ) {
        originalRequest._retry = true
        // The server already rejected this token — refresh even if the local
        // clock still considers it valid
        await tokenStore.updateTokens({ force: true })

        // Only retry if tokens were actually refreshed
        if (tokenStore.accessToken) {
          return client(originalRequest)
        }
      }

      return Promise.reject(error)
    }
  )
}

